// ============================================================
// xFreeScan by XCloud Solutions - Scan List Page (Light Theme)
// ============================================================
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { scansApi } from '../../lib/api';
import {
  Scan, Target, Clock, ChevronRight, Plus,
  CheckCircle, AlertCircle, Loader, RefreshCw
} from 'lucide-react';

export default function ScanListPage() {
  const [scans, setScans] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadScans();
  }, [page, statusFilter]);

  const loadScans = async () => {
    setIsLoading(true);
    try {
      const response = await scansApi.list({
        page,
        limit: 20,
        status: statusFilter || undefined,
      });
      if (response.success) {
        setScans(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Error loading scans:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'completed': return { label: 'Completado', color: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200', icon: CheckCircle };
      case 'running': return { label: 'En progreso', color: 'bg-purple-100 text-purple-700 ring-1 ring-purple-200', icon: Loader };
      case 'failed': return { label: 'Fallido', color: 'bg-red-100 text-red-700 ring-1 ring-red-200', icon: AlertCircle };
      case 'queued': return { label: 'En cola', color: 'bg-gray-100 text-gray-600 ring-1 ring-gray-200', icon: Clock };
      default: return { label: status, color: 'bg-gray-100 text-gray-600 ring-1 ring-gray-200', icon: Clock };
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mis Escaneos</h1>
          <p className="mt-1 text-gray-500">Historial de todos sus escaneos de seguridad</p>
        </div>
        <div className="flex gap-3">
          <button onClick={loadScans} className="btn-secondary text-sm">
            <RefreshCw className="h-4 w-4" /> Actualizar
          </button>
          <Link to="/app/scans/new" className="btn-primary text-sm">
            <Plus className="h-4 w-4" /> Nuevo Escaneo
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['', 'completed', 'running', 'queued', 'failed'].map((status) => (
          <button
            key={status}
            onClick={() => { setStatusFilter(status); setPage(1); }}
            className={`badge px-3 py-1.5 text-xs transition-colors cursor-pointer ${
              statusFilter === status
                ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-200'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {status === '' ? 'Todos' :
             status === 'completed' ? 'Completados' :
             status === 'running' ? 'En progreso' :
             status === 'queued' ? 'En cola' : 'Fallidos'}
          </button>
        ))}
      </div>

      {/* Scans list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple-600 border-t-transparent" />
        </div>
      ) : scans.length > 0 ? (
        <div className="space-y-3">
          {scans.map((scan) => {
            const statusInfo = getStatusInfo(scan.status);
            return (
              <Link
                key={scan.id}
                to={`/app/scans/${scan.id}`}
                className="card flex items-center gap-4 transition-all hover:border-purple-300 hover:shadow-lg"
              >
                <div className={`rounded-xl p-3 ${
                  scan.status === 'completed' ? 'bg-emerald-50' :
                  scan.status === 'running' ? 'bg-purple-50' : 'bg-gray-50'
                }`}>
                  <Target className={`h-6 w-6 ${
                    scan.status === 'completed' ? 'text-emerald-600' :
                    scan.status === 'running' ? 'text-purple-600' : 'text-gray-400'
                  }`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <p className="font-semibold text-gray-900 truncate">{scan.target}</p>
                    {scan.grade && (
                      <span className={`text-lg font-bold ${
                        scan.grade.startsWith('A') ? 'grade-a' :
                        scan.grade.startsWith('B') ? 'grade-b' :
                        scan.grade.startsWith('C') ? 'grade-c' :
                        scan.grade.startsWith('D') ? 'grade-d' : 'grade-f'
                      }`}>
                        {scan.grade}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-3">
                    <span className={`badge ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                    {scan.total_findings != null && (
                      <span className="text-xs text-gray-500">
                        {scan.total_findings} hallazgos
                      </span>
                    )}
                    {scan.score != null && (
                      <span className="text-xs text-gray-500">
                        Score: {scan.score}/100
                      </span>
                    )}
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(scan.created_at).toLocaleDateString('es', {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>

                <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="card text-center py-16">
          <Scan className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-4 text-lg text-gray-500">No se encontraron escaneos</p>
          <p className="mt-1 text-sm text-gray-400">Lance su primer escaneo para comenzar a analizar su seguridad.</p>
          <Link to="/app/scans/new" className="btn-primary mt-6 inline-flex">
            <Plus className="h-4 w-4" /> Crear Primer Escaneo
          </Link>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="btn-secondary text-sm px-4 py-2"
          >
            Anterior
          </button>
          <span className="text-sm text-gray-500">
            Pagina {page} de {pagination.pages}
          </span>
          <button
            onClick={() => setPage(Math.min(pagination.pages, page + 1))}
            disabled={page === pagination.pages}
            className="btn-secondary text-sm px-4 py-2"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}
